<template>
  <transition name="fade">
    <div
      v-if="visible"
      class="fixed inset-0 z-[1050] flex items-center justify-center p-0 sm:p-4"
    >
      <div class="absolute inset-0 bg-black/40" @click="handleClose" @wheel.prevent @touchmove.prevent></div>

      <div
        class="relative z-[1060] flex h-[100dvh] w-full max-w-none touch-manipulation flex-col overflow-hidden border-0 bg-white shadow-xl sm:h-[90dvh] sm:max-w-4xl sm:rounded-2xl sm:border sm:border-gray-100"
        @click.stop
        @wheel.stop
        @touchmove.stop
      >
        <div
          class="flex items-center justify-between gap-2 px-3 sm:px-6 py-2.5 sm:py-2 border-b border-gray-100 flex-shrink-0"
        >
          <div class="min-w-0 flex-shrink-0 max-w-[7rem] sm:max-w-none">
            <h3 class="text-base sm:text-lg font-semibold text-black truncate">
              {{
                venduPar === "melangees" ? "Nouvelle photo" : "Nouvelle couleur"
              }}
            </h3>
            <p v-if="product" class="text-xs text-black mt-0.5 truncate">
              Produit :
              <span class="font-medium"
                >{{ product.reference_base || product.reference }}</span
              >
            </p>
          </div>
          <div
            v-if="venduPar !== 'melangees' && currentStep === 1"
            class="relative ml-auto min-w-0 max-w-md flex-1"
          >
            <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
            <input
              ref="searchInputRef"
              v-model.trim="searchQuery"
              type="text"
              autocomplete="off"
              placeholder="Rechercher une couleur"
              class="w-full rounded-full border border-gray-300 py-2 pl-9 pr-9 text-sm focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
              role="combobox"
              :aria-expanded="isSearchDropdownOpen"
              @focus="openSearchDropdown"
              @input="openSearchDropdown"
              @blur="closeSearchDropdown"
              @keydown.down.prevent="moveSearchHighlight(1)"
              @keydown.up.prevent="moveSearchHighlight(-1)"
              @keydown.enter.prevent="selectHighlightedSearchColor"
              @keydown.tab.exact.prevent="focusConfirmButton"
            />
            <button
              v-if="searchQuery"
              type="button"
              tabindex="-1"
              class="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full p-1 text-gray-400 hover:text-black"
              aria-label="Effacer la recherche"
              @mousedown.prevent
              @click="clearSearch"
            >
              <i class="fas fa-times text-xs"></i>
            </button>
            <div
              v-if="isSearchDropdownOpen && searchQuery"
              class="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg"
              role="listbox"
            >
              <p v-if="!searchResults.length" class="px-3 py-3 text-sm text-gray-500">
                Aucune couleur trouvée
              </p>
              <button
                v-for="(color, index) in searchResults"
                :key="`search-${color.id_couleur}`"
                type="button"
                tabindex="-1"
                class="flex w-full items-center gap-2 border-b border-gray-100 px-3 py-2 text-left text-sm last:border-b-0"
                :class="[
                  index === highlightedSearchIndex ? 'bg-blue-100' : 'hover:bg-gray-100',
                  isColorDisabled(color.id_couleur) ? 'opacity-50' : '',
                ]"
                :disabled="isColorDisabled(color.id_couleur)"
                @mousedown.prevent="selectSearchColor(color)"
              >
                <span
                  class="color-swatch-dot h-4 w-4 flex-shrink-0 rounded-full"
                  :style="getColorSwatchStyle(color.label, color.hexColor)"
                ></span>
                <span class="truncate">{{ color.label }}</span>
                <span
                  v-if="isColorDisabled(color.id_couleur)"
                  class="ml-auto flex-shrink-0 text-xs text-gray-500"
                >Utilisée</span>
              </button>
            </div>
          </div>
          <span
            v-if="venduPar !== 'melangees' && currentStep === 1 && localSelectedIds.length"
            class="hidden text-xs font-medium text-black sm:inline sm:whitespace-nowrap"
          >
            {{ localSelectedIds.length }} sél.
          </span>
          <button
            type="button"
            class="p-2 rounded-full hover:bg-gray-100 text-black flex-shrink-0"
            @click="handleClose"
          >
            <span class="sr-only">Fermer</span>
            ✕
          </button>
        </div>

        <div
          class="flex-1 min-h-0 flex flex-col"
          :class="currentStep === 1 && venduPar !== 'melangees' ? 'overflow-hidden' : 'px-3 sm:px-6 py-2 overflow-y-auto'"
          style="overscroll-behavior: contain"
        >

          <!-- ===== MODE MELANGEES : upload direct ===== -->
          <div v-if="venduPar === 'melangees'">
            <p class="text-xs text-black mb-2">Ajoutez des photos pour ce produit.</p>
            <div
              class="mb-4 flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800"
            >
              <i class="fas fa-info-circle mt-0.5 flex-shrink-0"></i>
              <span>
                Si vous utilisez des photos IA, ajoutez également au moins une
                photo réelle du produit sur cintre.
              </span>
            </div>
            <h4 class="text-sm font-semibold text-black mb-3">Photos du produit</h4>
            <div class="flex flex-col gap-4 mb-6">
              <div v-if="melangeesFiles.length" class="relative md:order-2">
                <div class="flex gap-3 overflow-x-auto flex-nowrap">
                <div
                  v-for="(file, index) in melangeesFiles"
                  :key="index"
                  class="relative w-28 h-28 flex-shrink-0 bg-gray-100 rounded overflow-hidden flex items-center justify-center border border-gray-300 cursor-pointer"
                  @click="toggleOverlay('melangees', index)"
                >
                  <img
                    :src="getMelangeesPreview(file)"
                    :alt="`Photo ${index + 1}`"
                    class="max-w-full max-h-full object-contain rounded"
                    :style="{ transform: `rotate(${getMelangeesRotation(file)}deg)` }"
                  />
                  <div
                    v-if="melangesCoverIndex === index"
                    class="absolute top-0 left-0 right-0 bg-blue-500 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded-b text-center leading-tight"
                  >
                    Photo de<br />couverture
                  </div>
                  <div
                    class="absolute inset-0 bg-black/40 transition-opacity grid grid-cols-2 place-items-center gap-1 p-4"
                    :class="activeOverlay === `melangees-${index}` ? 'opacity-100' : 'opacity-0 pointer-events-none'"
                  >
                    <button type="button"
                      class="p-2 bg-white/90 rounded-full hover:bg-white focus:outline-none transition-transform hover:scale-90"
                      :class="melangesCoverIndex === index ? 'text-yellow-500' : 'text-black'"
                      title="Définir comme photo de couverture"
                      @click.stop="melangesCoverIndex = index"
                    ><i class="fas fa-star text-sm"></i></button>
                    <button type="button"
                      class="p-2 bg-white/90 text-blue-600 rounded-full hover:bg-white focus:outline-none transition-transform hover:scale-90"
                      title="Pivoter la photo"
                      @click.stop="rotateMelangeesPhoto(index)"
                    ><i class="fas fa-redo-alt text-sm"></i></button>
                    <a :href="getMelangeesPreview(file)" :download="file.name"
                      class="p-2 bg-white/90 text-black rounded-full hover:bg-white focus:outline-none transition-transform hover:scale-90 flex items-center justify-center"
                      title="Télécharger" @click.stop
                    ><i class="fas fa-download text-sm"></i></a>
                    <button type="button"
                      class="p-2 bg-white/90 text-red-600 rounded-full hover:bg-white focus:outline-none transition-transform hover:scale-90"
                      title="Supprimer la photo"
                      @click.stop="removeMelangeesFile(index)"
                    ><i class="fas fa-trash-alt text-sm"></i></button>
                  </div>
                </div>
                </div>
                <div v-if="melangeesFiles.length > 2" class="absolute right-0 top-0 bottom-0 w-8 flex items-center justify-center bg-gradient-to-l from-white via-white/80 to-transparent pointer-events-none">
                  <i class="fas fa-chevron-right text-black text-sm"></i>
                </div>
              </div>
              <div
                class="border-2 border-dashed rounded-lg p-2 md:p-6 text-center transition-colors md:order-1"
                :class="isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'"
                @dragenter.prevent="isDragging = true"
                @dragover.prevent
                @dragleave.prevent="isDragging = false"
                @drop.prevent="handleDropMelangees"
              >
                <div class="text-black">
                  <i class="fas fa-cloud-upload-alt text-lg md:text-3xl mb-0.5 md:mb-2"></i>
                  <p class="text-[11px] md:text-sm">Glissez-déposez ou cliquez</p>
                  <button
                    type="button"
                    class="mt-1 md:mt-2 px-3 md:px-4 py-1 md:py-2 text-xs md:text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    @click="triggerFileDialogMelangees"
                  >
                    Parcourir
                  </button>
                  <p class="text-[10px] md:text-xs text-black mt-0.5 md:mt-2">Les photos uploadées ne peuvent pas dépasser 15 Mo.</p>
                </div>
              </div>
            </div>
          </div>

          <!-- ===== MODE COULEUR : ÉTAPE 1 — Sélection des couleurs ===== -->
          <div v-else-if="currentStep === 1" class="flex flex-col flex-1 min-h-0">
            <!-- Mobile uniquement : familles en chips horizontales -->
            <div
              class="flex md:hidden flex-col border-b border-gray-100 flex-shrink-0 overscroll-contain"
            >
              <template v-if="visibleColorFamilies.length">
                <button
                  type="button"
                  class="flex items-center justify-between px-3 py-2 text-sm font-bold text-black uppercase tracking-wide"
                  @click="toggleCouleursSection"
                >
                  <span>Couleurs</span>
                  <i
                    class="fas fa-chevron-down collapse-chevron text-[10px] text-gray-400"
                    :class="{ 'collapse-chevron--open': couleursSectionOpen }"
                  ></i>
                </button>
                <div
                  class="collapse-panel"
                  :class="couleursSectionOpen ? 'collapse-panel--open' : 'collapse-panel--closed'"
                >
                  <div class="collapse-panel__inner">
                    <div class="flex gap-2 px-3 pb-2 overflow-x-auto">
                  <button
                    v-for="family in visibleColorFamilies"
                    :key="'mc-' + family.id"
                    type="button"
                    class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs whitespace-nowrap border flex-shrink-0 transition-colors"
                    :class="navButtonClass('couleurs', family.id)"
                    @click="selectColorFamily(family.id)"
                  >
                    <span
                      class="color-swatch-dot w-3.5 h-3.5 rounded-full flex-shrink-0"
                      :style="familySwatchStyle(family)"
                    ></span>
                    {{ family.label }}
                    <span
                      v-if="pendingCountByColorFamily[family.id]"
                      class="opacity-80"
                    >({{ pendingCountByColorFamily[family.id] }})</span>
                  </button>
                    </div>
                  </div>
                </div>
              </template>
              <template v-if="visibleMotifCategories.length">
                <button
                  type="button"
                  class="flex items-center justify-between px-3 py-2 text-sm font-bold text-black uppercase tracking-wide"
                  :class="visibleColorFamilies.length ? 'border-t border-gray-100' : ''"
                  @click="toggleMotifsSection"
                >
                  <span>Motifs</span>
                  <i
                    class="fas fa-chevron-down collapse-chevron text-[10px] text-gray-400"
                    :class="{ 'collapse-chevron--open': motifsSectionOpen }"
                  ></i>
                </button>
                <div
                  class="collapse-panel"
                  :class="motifsSectionOpen ? 'collapse-panel--open' : 'collapse-panel--closed'"
                >
                  <div class="collapse-panel__inner">
                    <div class="flex gap-2 px-3 pb-2 overflow-x-auto">
                  <button
                    v-for="category in visibleMotifCategories"
                    :key="'mm-' + category.id"
                    type="button"
                    class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs whitespace-nowrap border flex-shrink-0 transition-colors"
                    :class="navButtonClass('motifs', category.id)"
                    @click="selectMotifCategory(category.id)"
                  >
                    {{ category.label }}
                    <span
                      v-if="pendingCountByMotifCategory[category.id]"
                      class="opacity-80"
                    >({{ pendingCountByMotifCategory[category.id] }})</span>
                  </button>
                    </div>
                  </div>
                </div>
              </template>
            </div>

            <div class="flex flex-1 min-h-0 overflow-hidden">
              <!-- Desktop : sidebar -->
              <aside
                class="hidden md:block w-44 lg:w-48 flex-shrink-0 bg-gray-50 border-r border-gray-200 overflow-y-auto overscroll-contain"
              >
                <template v-if="visibleColorFamilies.length">
                  <button
                    type="button"
                    class="w-full flex items-center justify-between px-3 py-2.5 text-sm font-bold text-black uppercase tracking-wide hover:bg-white/70 transition-colors"
                    @click="toggleCouleursSection"
                  >
                    <span>Couleurs</span>
                    <i
                      class="fas fa-chevron-down collapse-chevron text-[10px] text-gray-400"
                      :class="{ 'collapse-chevron--open': couleursSectionOpen }"
                    ></i>
                  </button>
                  <div
                    class="collapse-panel"
                    :class="couleursSectionOpen ? 'collapse-panel--open' : 'collapse-panel--closed'"
                  >
                    <div class="collapse-panel__inner collapse-panel__inner--sidebar">
                    <button
                      v-for="family in visibleColorFamilies"
                      :key="family.id"
                      type="button"
                      class="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left transition-colors"
                      :class="sidebarItemClass('couleurs', family.id)"
                      @click="selectColorFamily(family.id)"
                    >
                      <span
                        class="color-swatch-dot w-5 h-5 rounded-full flex-shrink-0"
                        :style="familySwatchStyle(family)"
                      ></span>
                      <span class="truncate flex-1">{{ family.label }}</span>
                      <span
                        v-if="pendingCountByColorFamily[family.id]"
                        class="text-xs text-gray-500"
                      >
                        ({{ pendingCountByColorFamily[family.id] }})
                      </span>
                    </button>
                    </div>
                  </div>
                </template>

                <template v-if="visibleMotifCategories.length">
                  <button
                    type="button"
                    class="w-full flex items-center justify-between px-3 py-2.5 text-sm font-bold text-black uppercase tracking-wide hover:bg-white/70 transition-colors"
                    :class="visibleColorFamilies.length ? 'border-t border-gray-200' : ''"
                    @click="toggleMotifsSection"
                  >
                    <span>Motifs</span>
                    <i
                      class="fas fa-chevron-down collapse-chevron text-[10px] text-gray-400"
                      :class="{ 'collapse-chevron--open': motifsSectionOpen }"
                    ></i>
                  </button>
                  <div
                    class="collapse-panel"
                    :class="motifsSectionOpen ? 'collapse-panel--open' : 'collapse-panel--closed'"
                  >
                    <div class="collapse-panel__inner collapse-panel__inner--sidebar">
                    <button
                      v-for="category in visibleMotifCategories"
                      :key="category.id"
                      type="button"
                      class="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left transition-colors"
                      :class="sidebarItemClass('motifs', category.id)"
                      @click="selectMotifCategory(category.id)"
                    >
                      <span class="truncate flex-1">{{ category.label }}</span>
                      <span
                        v-if="pendingCountByMotifCategory[category.id]"
                        class="text-xs text-gray-500"
                      >
                        ({{ pendingCountByMotifCategory[category.id] }})
                      </span>
                    </button>
                    </div>
                  </div>
                </template>
              </aside>

              <section class="flex-1 overflow-y-auto overscroll-contain p-3 sm:p-5">
                <h4
                  v-if="panelTitle"
                  class="text-sm font-medium text-black mb-3 capitalize"
                >
                  {{ panelTitle }}
                </h4>
                <div
                  v-if="displayedShades.length === 0"
                  class="text-sm text-gray-500 py-8 text-center"
                >
                  {{
                    searchQuery
                      ? "Aucune couleur trouvée"
                      : activeSection === "motifs"
                        ? "Aucun motif dans cette famille"
                        : "Aucune couleur dans cette famille"
                  }}
                </div>
                <div
                  v-else
                  class="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-4 sm:gap-x-6 sm:gap-y-5"
                >
                  <button
                    v-for="color in displayedShades"
                    :key="color.id_couleur"
                    type="button"
                    class="group text-left focus:outline-none"
                    :class="[
                      isColorDisabled(color.id_couleur)
                        ? 'opacity-40 cursor-not-allowed'
                        : '',
                      activeSection === 'motifs'
                        ? localSelectedIds.includes(color.id_couleur)
                          ? 'rounded-md border border-black ring-1 ring-black px-2.5 py-2'
                          : 'rounded-md border border-gray-200 px-2.5 py-2 hover:border-gray-400'
                        : '',
                    ]"
                    :title="
                      isColorDisabled(color.id_couleur)
                        ? 'Cette couleur est déjà utilisée'
                        : ''
                    "
                    :disabled="isColorDisabled(color.id_couleur)"
                    @click="toggleColor(color.id_couleur)"
                  >
                    <div
                      class="text-xs sm:text-sm text-black truncate flex items-center justify-between gap-2"
                      :class="activeSection === 'motifs' ? '' : 'mb-1.5'"
                    >
                      <span class="truncate">
                        {{ color.label }}
                        <span
                          v-if="isColorDisabled(color.id_couleur)"
                          class="text-[10px] text-gray-500"
                        >(utilisée)</span>
                      </span>
                      <i
                        v-if="
                          activeSection === 'motifs' &&
                          localSelectedIds.includes(color.id_couleur)
                        "
                        class="fas fa-check text-[10px] flex-shrink-0"
                      ></i>
                    </div>
                    <div
                      v-if="activeSection !== 'motifs'"
                      class="relative w-full h-12 sm:h-16 rounded-md border overflow-hidden transition-all"
                      :class="
                        localSelectedIds.includes(color.id_couleur)
                          ? 'border-black ring-1 ring-black'
                          : 'border-gray-200 group-hover:border-gray-400'
                      "
                      :style="getColorSwatchStyle(color.label, color.hexColor)"
                    >
                      <div
                        v-if="localSelectedIds.includes(color.id_couleur)"
                        class="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-black text-white flex items-center justify-center shadow"
                      >
                        <i class="fas fa-check text-[8px]"></i>
                      </div>
                    </div>
                  </button>
                </div>
              </section>
            </div>
          </div>

          <!-- ===== MODE COULEUR : ÉTAPE 2 — Upload photos par couleur ===== -->
          <div v-else-if="currentStep === 2">
            <div class="mb-4 text-xs text-black">
              Ajoutez des photos pour chaque couleur sélectionnée. Cliquez sur l'onglet d'une couleur pour gérer ses photos.
              <span class="block mt-1 text-amber-600">
                <i class="fas fa-info-circle mr-1"></i>
                Si vous utilisez des photos IA, ajoutez également au moins une photo réelle du produit sur cintre.
              </span>
            </div>

            <!-- Onglets des couleurs -->
            <div class="flex flex-wrap gap-2 border-b border-gray-200 mb-4">
              <button
                v-for="colorId in localSelectedIds"
                :key="colorId"
                type="button"
                :class="[
                  'px-3 py-1.5 text-sm rounded-t-lg border-b-2 transition-colors flex items-center gap-1.5',
                  activeColorId === colorId
                    ? 'border-blue-600 text-blue-600 font-medium bg-blue-50'
                    : 'border-transparent text-black hover:text-black hover:bg-gray-50',
                ]"
                @click="activeColorId = colorId"
              >
                {{ getColorLabel(colorId) }}
                <span
                  v-if="getColorPhotoCount(colorId) > 0"
                  class="inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold rounded-full"
                  :class="activeColorId === colorId ? 'bg-blue-600 text-white' : 'bg-gray-300 text-black'"
                >{{ getColorPhotoCount(colorId) }}</span>
                <span
                  v-else
                  class="inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold rounded-full bg-amber-100 text-amber-500"
                  title="Aucune photo — sera créée en brouillon"
                >!</span>
              </button>
            </div>

            <!-- Zone d'upload pour la couleur active -->
            <div v-if="activeColorId !== null">
              <p class="text-sm text-black mb-3">
                Photos pour <strong class="text-blue-700">{{ getColorLabel(activeColorId) }}</strong>
              </p>

              <div v-if="getColorFiles(activeColorId).length" class="relative mb-3">
                <div class="flex gap-3 overflow-x-auto flex-nowrap">
                <div
                  v-for="(file, index) in getColorFiles(activeColorId)"
                  :key="index"
                  class="relative w-28 h-28 flex-shrink-0 bg-gray-100 rounded overflow-hidden flex items-center justify-center border border-gray-300 cursor-move"
                  :class="[
                    draggingColorPhotoIndex === index ? 'opacity-50 scale-95' : '',
                    dragOverColorPhotoIndex === index ? 'ring-2 ring-blue-400 ring-offset-1' : '',
                  ]"
                  draggable="true"
                  @click="toggleOverlay('color', index)"
                  @dragstart="handleColorPhotoDragStart($event, index)"
                  @dragend="handleColorPhotoDragEnd()"
                  @dragover="handleColorPhotoDragOver($event, index)"
                  @drop="handleColorPhotoDrop($event, activeColorId, index)"
                >
                  <img
                    :src="getColorFilePreview(activeColorId, file)"
                    :alt="`Photo ${index + 1}`"
                    class="max-w-full max-h-full object-contain rounded pointer-events-none"
                    :style="{ transform: `rotate(${getColorFileRotation(activeColorId, file)}deg)` }"
                  />
                  <div
                    v-if="getColorCoverIndex(activeColorId) === index"
                    class="absolute top-0 left-0 right-0 bg-blue-500 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded-b text-center leading-tight"
                  >
                    Photo de<br />couverture
                  </div>
                  <div
                    class="absolute inset-0 bg-black/40 transition-opacity grid grid-cols-2 place-items-center gap-1 p-4"
                    :class="activeOverlay === `color-${index}` ? 'opacity-100' : 'opacity-0 pointer-events-none'"
                  >
                    <button type="button"
                      class="p-2 bg-white/90 rounded-full hover:bg-white focus:outline-none transition-transform hover:scale-90"
                      :class="getColorCoverIndex(activeColorId) === index ? 'text-yellow-500' : 'text-black'"
                      title="Définir comme photo de couverture"
                      @click.stop="setColorCover(activeColorId, index)"
                    ><i class="fas fa-star text-sm"></i></button>
                    <button type="button"
                      class="p-2 bg-white/90 text-blue-600 rounded-full hover:bg-white focus:outline-none transition-transform hover:scale-90"
                      title="Pivoter la photo"
                      @click.stop="rotateColorPhoto(activeColorId, index)"
                    ><i class="fas fa-redo-alt text-sm"></i></button>
                    <a :href="getColorFilePreview(activeColorId, file)" :download="file.name"
                      class="p-2 bg-white/90 text-black rounded-full hover:bg-white focus:outline-none transition-transform hover:scale-90 flex items-center justify-center"
                      title="Télécharger" @click.stop
                    ><i class="fas fa-download text-sm"></i></a>
                    <button type="button"
                      class="p-2 bg-white/90 text-red-600 rounded-full hover:bg-white focus:outline-none transition-transform hover:scale-90"
                      title="Supprimer la photo"
                      @click.stop="removeColorFile(activeColorId, index)"
                    ><i class="fas fa-trash-alt text-sm"></i></button>
                  </div>
                </div>
                </div>
                <div v-if="getColorFiles(activeColorId).length > 2" class="absolute right-0 top-0 bottom-0 w-8 flex items-center justify-center bg-gradient-to-l from-white via-white/80 to-transparent pointer-events-none">
                  <i class="fas fa-chevron-right text-black text-sm"></i>
                </div>
              </div>

              <div
                class="border-2 border-dashed rounded-lg p-2 md:p-6 text-center transition-colors"
                :class="isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'"
                @dragenter.prevent="isDragging = true"
                @dragover.prevent
                @dragleave.prevent="isDragging = false"
                @drop.prevent="handleDropForColor($event, activeColorId)"
              >
                <div class="text-black">
                  <i class="fas fa-cloud-upload-alt text-lg md:text-3xl mb-0.5 md:mb-2"></i>
                  <p class="text-[11px] md:text-sm">Glissez-déposez ou cliquez</p>
                  <button
                    type="button"
                    class="mt-1 md:mt-2 px-3 md:px-4 py-1 md:py-2 text-xs md:text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    @click="triggerFileDialogForColor(activeColorId)"
                  >
                    Parcourir
                  </button>
                  <p class="text-[10px] md:text-xs text-black mt-0.5 md:mt-2">Les photos uploadées ne peuvent pas dépasser 15 Mo.</p>
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- Footer -->
        <div class="px-3 sm:px-6 py-3 sm:py-4 border-t border-gray-100 flex-shrink-0 safe-area-pb">
          <div
            v-if="uploadError"
            class="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700"
          >
            <i class="fas fa-exclamation-circle mr-1"></i>{{ uploadError }}
          </div>
          <div class="flex justify-between gap-2 sm:gap-3">
            <!-- Bouton retour (étape 2 uniquement) -->
            <button
              v-if="venduPar !== 'melangees' && currentStep === 2"
              type="button"
              class="px-3 sm:px-4 py-2 text-sm rounded-lg border border-gray-300 text-black hover:bg-gray-50"
              @click="currentStep = 1"
            >
              Retour
            </button>
            <div v-else></div>

            <div class="flex flex-wrap justify-end gap-2 sm:gap-3">
              <!-- Étape 1 : bouton "Suivant" -->
              <button
                v-if="venduPar !== 'melangees' && currentStep === 1"
                ref="confirmButtonRef"
                type="button"
                class="px-4 py-2.5 sm:py-2 text-sm rounded-lg bg-black text-white hover:bg-gray-800 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="!localSelectedIds.length"
                @click="goToStep2"
                @keydown.tab.exact.prevent="focusSearchInput"
              >
                Suivant : Photos
              </button>

              <!-- Étape 2 : bouton "Créer les variantes" -->
              <button
                v-else-if="venduPar !== 'melangees' && currentStep === 2"
                type="button"
                class="px-3 sm:px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="isUploading"
                @click="handleSave"
              >
                <span v-if="isUploading"><i class="fas fa-spinner fa-spin mr-1"></i>Upload...</span>
                <span v-else>Créer {{ localSelectedIds.length }} variante{{ localSelectedIds.length > 1 ? 's' : '' }}</span>
              </button>

              <!-- Étape 2 : bouton "Mettre en ligne" -->
              <button
                v-if="venduPar !== 'melangees' && currentStep === 2"
                type="button"
                class="px-3 sm:px-4 py-2 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="isUploading || !allColorsHavePhotos"
                @click="handleSaveAndPublish"
              >
                <span v-if="isUploading"><i class="fas fa-spinner fa-spin mr-1"></i>Upload...</span>
                <span v-else><i class="fas fa-cloud-upload-alt mr-1"></i>En ligne</span>
              </button>

              <!-- Mode mélangées -->
              <button
                v-else-if="venduPar === 'melangees'"
                type="button"
                class="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="isUploading || !melangeesFiles.length"
                @click="handleSave"
              >
                <span v-if="isUploading"><i class="fas fa-spinner fa-spin mr-1"></i>Upload...</span>
                <span v-else>Ajouter les photos</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch, onBeforeUnmount } from "vue";
import type { ProduitCatalogue } from "@/types";
import type { CouleurAPI } from "@/types/mel-components";
import {
  type BasicColorFamily,
  type BasicColorFamilyId,
  getColorSwatchStyle,
  getFamiliesWithVendorColors,
  groupVendorColorsByBasicFamily,
} from "@/utils/colorFamilies";
import {
  type MotifCategory,
  formatMotifPanelTitle,
  getMotifCategoriesWithColors,
  groupMotifsByCategory,
  splitSolidAndMotifColors,
} from "@/utils/motifFamilies";

type PickerSection = "couleurs" | "motifs";

export interface ColorOption {
  id_couleur: number;
  label: string;
  hexColor?: string | null;
  motifCategorie?: string | null;
  idCouleurEfashion?: number | null;
  officiel?: number | null;
}

interface Props {
  visible: boolean;
  product: ProduitCatalogue | null;
  sourceProductId?: number | null;
  colors: ColorOption[];
  disabledColorIds?: number[];
  venduPar?: string;
}

interface ColorPhotoEntry {
  files: File[];
  previews: Map<File, string>;
  rotations: Map<File, number>;
  coverIndex: number;
}

interface Emits {
  (e: "update:visible", value: boolean): void;
  (
    e: "save",
    payload: {
      productId: number;
      variants: Array<{
        couleurId: number;
        couleurName: string;
        photos?: File[];
        rotations?: number[];
      }>;
    },
  ): void;
  (
    e: "saveAndPublish",
    payload: {
      productId: number;
      variants: Array<{
        couleurId: number;
        couleurName: string;
        photos?: File[];
        rotations?: number[];
      }>;
    },
  ): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const duplicateSourceProductId = computed(() => {
  const fromLiaison = Number(props.sourceProductId);
  if (!Number.isNaN(fromLiaison) && fromLiaison > 0) return fromLiaison;
  return Number(props.product?.id) || 0;
});

// Étape courante (1 = sélection couleurs, 2 = upload photos)
const currentStep = ref(1);

// Sélection multiple de couleurs
const localSelectedIds = ref<number[]>([]);

// Couleur active dans l'onglet (étape 2)
const activeColorId = ref<number | null>(null);

// Stockage des photos par couleur
const colorPhotos = ref<Map<number, ColorPhotoEntry>>(new Map());

// Recherche de couleurs
const searchQuery = ref<string>("");
const searchInputRef = ref<HTMLInputElement | null>(null);
const confirmButtonRef = ref<HTMLButtonElement | null>(null);
const isSearchDropdownOpen = ref(false);
const highlightedSearchIndex = ref(-1);
const activeSection = ref<PickerSection>("couleurs");
const activeFamilyId = ref<BasicColorFamilyId>("beige");
const activeMotifCategory = ref<MotifCategory>("Animaux");
const couleursSectionOpen = ref(true);
const motifsSectionOpen = ref(false);

// Mode mélangées : fichiers séparés
const melangeesFiles = ref<File[]>([]);
const melangeesFilePreviews = ref<Map<File, string>>(new Map());
const melangeesFileRotations = ref<Map<File, number>>(new Map());
const melangesCoverIndex = ref<number>(0);

const isDragging = ref(false);
const isUploading = ref(false);
const uploadError = ref<string | null>(null);

const activeOverlay = ref<string | null>(null);

function toggleOverlay(mode: string, index: number) {
  const key = `${mode}-${index}`;
  activeOverlay.value = activeOverlay.value === key ? null : key;
}

const ALLOWED_PHOTO_TYPES = new Set([
  "image/jpeg",
  "image/pjpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/x-adobe-dng",
  "image/dng",
]);

const disabledIds = computed(() => {
  const ids = props.disabledColorIds || [];
  return ids.map((id) => Number(id)).filter((n) => !Number.isNaN(n));
});

const isColorDisabled = (id: number | string) =>
  disabledIds.value.includes(Number(id));

watch(
  () => props.visible,
  (val) => {
    if (val) {
      localSelectedIds.value = [];
      searchQuery.value = "";
      currentStep.value = 1;
      activeColorId.value = null;
      activeSection.value = "couleurs";
      activeFamilyId.value =
        getFamiliesWithVendorColors(
          props.colors.map((c) => ({
            id_couleur: c.id_couleur,
            couleur_FR: c.label,
            couleur_EN: null,
            hexColor: c.hexColor ?? null,
            motifCategorie: c.motifCategorie ?? null,
          })).filter((c) => !c.motifCategorie),
        )[0]?.id || "beige";
      activeMotifCategory.value =
        getMotifCategoriesWithColors(
          props.colors
            .filter((c) => c.motifCategorie)
            .map((c) => ({
              id_couleur: c.id_couleur,
              couleur_FR: c.label,
              couleur_EN: null,
              hexColor: c.hexColor ?? null,
              motifCategorie: c.motifCategorie ?? null,
            })),
        )[0]?.id || "Animaux";

      const solidFamilies = getFamiliesWithVendorColors(
        props.colors
          .filter((c) => !c.motifCategorie)
          .map((c) => ({
            id_couleur: c.id_couleur,
            couleur_FR: c.label,
            couleur_EN: null,
            hexColor: c.hexColor ?? null,
          })),
      );
      const motifCats = getMotifCategoriesWithColors(
        props.colors
          .filter((c) => c.motifCategorie)
          .map((c) => ({
            id_couleur: c.id_couleur,
            couleur_FR: c.label,
            couleur_EN: null,
            hexColor: c.hexColor ?? null,
            motifCategorie: c.motifCategorie ?? null,
          })),
      );

      if (solidFamilies.length) {
        activeSection.value = "couleurs";
        activeFamilyId.value = solidFamilies[0]?.id || "beige";
        couleursSectionOpen.value = true;
        motifsSectionOpen.value = false;
      } else if (motifCats.length) {
        activeSection.value = "motifs";
        activeMotifCategory.value = motifCats[0].id;
        couleursSectionOpen.value = false;
        motifsSectionOpen.value = true;
      }
      colorPhotos.value = new Map();
      melangeesFiles.value = [];
      melangeesFilePreviews.value = new Map();
      melangeesFileRotations.value = new Map();
      melangesCoverIndex.value = 0;
      uploadError.value = null;
      document.body.style.overflow = "hidden";
      void nextTick(() => searchInputRef.value?.focus());
    } else {
      document.body.style.overflow = "";
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  document.body.style.overflow = "";
});

const normalize = (s: string) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}+/gu, "");

const colorsAsApi = computed<CouleurAPI[]>(() =>
  props.colors.map((c) => ({
    id_couleur: c.id_couleur,
    couleur_FR: c.label,
    couleur_EN: null,
    hexColor: c.hexColor ?? null,
    motifCategorie: c.motifCategorie ?? null,
    idCouleurEfashion: c.idCouleurEfashion ?? null,
    officiel: c.officiel ?? 1,
  })),
);

const solidColorsRef = computed(
  () => splitSolidAndMotifColors(colorsAsApi.value).solid,
);
const motifColorsRef = computed(
  () => splitSolidAndMotifColors(colorsAsApi.value).motifs,
);

const colorGroups = computed(() =>
  groupVendorColorsByBasicFamily(solidColorsRef.value),
);

const motifGroups = computed(() => groupMotifsByCategory(motifColorsRef.value));

const matchesSearch = (color: CouleurAPI, term: string) =>
  normalize(color.couleur_FR || "").includes(term);

const searchResults = computed(() => {
  const term = normalize(searchQuery.value);
  if (!term) return [];
  return props.colors.filter(
    (color) =>
      !localSelectedIds.value.includes(color.id_couleur) &&
      normalize(color.label).includes(term),
  );
});

const visibleColorFamilies = computed(() => {
  if (searchQuery.value) {
    const term = normalize(searchQuery.value);
    return getFamiliesWithVendorColors(solidColorsRef.value).filter((family) =>
      (colorGroups.value[family.id] || []).some((color) =>
        matchesSearch(color, term),
      ),
    );
  }
  return getFamiliesWithVendorColors(solidColorsRef.value);
});

const visibleMotifCategories = computed(() => {
  if (searchQuery.value) {
    const term = normalize(searchQuery.value);
    return getMotifCategoriesWithColors(motifColorsRef.value).filter(
      (category) =>
        (motifGroups.value[category.id] || []).some((color) =>
          matchesSearch(color, term),
        ),
    );
  }
  return getMotifCategoriesWithColors(motifColorsRef.value);
});

const displayedShades = computed(() => {
  const term = searchQuery.value ? normalize(searchQuery.value) : "";

  if (activeSection.value === "motifs") {
    let list = (motifGroups.value[activeMotifCategory.value] || []).map(
      (color) => ({
        id_couleur: Number(color.id_couleur),
        label: color.couleur_FR || "",
        hexColor: color.hexColor ?? null,
      }),
    );
    if (term) list = list.filter((color) => normalize(color.label).includes(term));
    return list;
  }

  let list = (colorGroups.value[activeFamilyId.value] || []).map((color) => ({
    id_couleur: Number(color.id_couleur),
    label: color.couleur_FR || "",
    hexColor: color.hexColor ?? null,
  }));
  if (term) list = list.filter((color) => normalize(color.label).includes(term));
  return list;
});

const panelTitle = computed(() => {
  if (activeSection.value === "motifs") {
    return formatMotifPanelTitle(activeMotifCategory.value);
  }
  const family = visibleColorFamilies.value.find(
    (item) => item.id === activeFamilyId.value,
  );
  return family ? `Nuances de ${family.label.toLowerCase()}` : "";
});

const pendingCountByColorFamily = computed(() => {
  const counts: Partial<Record<BasicColorFamilyId, number>> = {};
  for (const family of visibleColorFamilies.value) {
    const count = (colorGroups.value[family.id] || []).filter((color) =>
      localSelectedIds.value.includes(Number(color.id_couleur)),
    ).length;
    if (count > 0) counts[family.id] = count;
  }
  return counts;
});

const pendingCountByMotifCategory = computed(() => {
  const counts: Partial<Record<MotifCategory, number>> = {};
  for (const category of visibleMotifCategories.value) {
    const count = (motifGroups.value[category.id] || []).filter((color) =>
      localSelectedIds.value.includes(Number(color.id_couleur)),
    ).length;
    if (count > 0) counts[category.id] = count;
  }
  return counts;
});

const isActiveNav = (section: PickerSection, id: string) =>
  activeSection.value === section &&
  (section === "couleurs"
    ? activeFamilyId.value === id
    : activeMotifCategory.value === id);

const navButtonClass = (section: PickerSection, id: string) =>
  isActiveNav(section, id)
    ? "bg-black text-white border-black"
    : "bg-white text-black border-gray-300";

const sidebarItemClass = (section: PickerSection, id: string) =>
  isActiveNav(section, id)
    ? "bg-white text-black font-medium shadow-sm"
    : "text-gray-700 hover:bg-white/70";

const selectColorFamily = (familyId: BasicColorFamilyId) => {
  activeSection.value = "couleurs";
  activeFamilyId.value = familyId;
  couleursSectionOpen.value = true;
  motifsSectionOpen.value = false;
};

const selectMotifCategory = (category: MotifCategory) => {
  activeSection.value = "motifs";
  activeMotifCategory.value = category;
  motifsSectionOpen.value = true;
  couleursSectionOpen.value = false;
};

const toggleCouleursSection = () => {
  const willOpen = !couleursSectionOpen.value;
  couleursSectionOpen.value = willOpen;
  if (willOpen) motifsSectionOpen.value = false;
};

const toggleMotifsSection = () => {
  const willOpen = !motifsSectionOpen.value;
  motifsSectionOpen.value = willOpen;
  if (willOpen) couleursSectionOpen.value = false;
};

const familySwatchStyle = (family: BasicColorFamily) => {
  if (family.swatch.startsWith("linear-gradient")) {
    return { backgroundImage: family.swatch };
  }
  return { backgroundColor: family.swatch };
};

watch([visibleColorFamilies, visibleMotifCategories], ([families, categories]) => {
  if (
    activeSection.value === "couleurs" &&
    !families.find((family) => family.id === activeFamilyId.value)
  ) {
    if (families[0]) {
      activeFamilyId.value = families[0].id;
    } else if (categories[0]) {
      activeSection.value = "motifs";
      activeMotifCategory.value = categories[0].id;
      motifsSectionOpen.value = true;
      couleursSectionOpen.value = false;
    }
  }

  if (
    activeSection.value === "motifs" &&
    !categories.find((category) => category.id === activeMotifCategory.value)
  ) {
    if (categories[0]) {
      activeMotifCategory.value = categories[0].id;
    } else if (families[0]) {
      activeSection.value = "couleurs";
      activeFamilyId.value = families[0].id;
      couleursSectionOpen.value = true;
      motifsSectionOpen.value = false;
    }
  }
});

watch(searchQuery, () => {
  if (!searchQuery.value) return;
  if (visibleColorFamilies.value[0]) {
    activeSection.value = "couleurs";
    activeFamilyId.value = visibleColorFamilies.value[0].id;
    couleursSectionOpen.value = true;
    motifsSectionOpen.value = false;
    return;
  }
  if (visibleMotifCategories.value[0]) {
    activeSection.value = "motifs";
    activeMotifCategory.value = visibleMotifCategories.value[0].id;
    motifsSectionOpen.value = true;
    couleursSectionOpen.value = false;
  }
});

const toggleColor = (id: number) => {
  if (isColorDisabled(id)) return;
  const index = localSelectedIds.value.indexOf(id);
  if (index === -1) {
    localSelectedIds.value.push(id);
  } else {
    localSelectedIds.value.splice(index, 1);
    const entry = colorPhotos.value.get(id);
    if (entry) {
      entry.previews.forEach((url) => URL.revokeObjectURL(url));
      colorPhotos.value.delete(id);
    }
    if (activeColorId.value === id) {
      activeColorId.value = localSelectedIds.value[0] ?? null;
    }
  }
};

const showSearchColorPage = (colorId: number) => {
  const motifCategory = visibleMotifCategories.value.find((category) =>
    (motifGroups.value[category.id] || []).some(
      (color) => Number(color.id_couleur) === colorId,
    ),
  );
  if (motifCategory) {
    selectMotifCategory(motifCategory.id);
    return;
  }
  const family = visibleColorFamilies.value.find((item) =>
    (colorGroups.value[item.id] || []).some(
      (color) => Number(color.id_couleur) === colorId,
    ),
  );
  if (family) selectColorFamily(family.id);
};

const selectSearchColor = (color: ColorOption) => {
  showSearchColorPage(color.id_couleur);
  toggleColor(color.id_couleur);
  clearSearch();
};

const clearSearch = () => {
  searchQuery.value = "";
  isSearchDropdownOpen.value = false;
  highlightedSearchIndex.value = -1;
  void nextTick(() => searchInputRef.value?.focus());
};

const openSearchDropdown = (event?: Event) => {
  const value =
    event?.target instanceof HTMLInputElement
      ? event.target.value
      : searchQuery.value;
  isSearchDropdownOpen.value = Boolean(value.trim());
};

const closeSearchDropdown = () => {
  isSearchDropdownOpen.value = false;
};

const moveSearchHighlight = (delta: number) => {
  isSearchDropdownOpen.value = true;
  const length = searchResults.value.length;
  if (!length) return;
  if (highlightedSearchIndex.value < 0) {
    highlightedSearchIndex.value = delta > 0 ? 0 : length - 1;
  } else {
    highlightedSearchIndex.value =
      (highlightedSearchIndex.value + delta + length) % length;
  }
};

const selectHighlightedSearchColor = () => {
  const color =
    searchResults.value[
      highlightedSearchIndex.value >= 0 ? highlightedSearchIndex.value : 0
    ];
  if (color && !isColorDisabled(color.id_couleur)) selectSearchColor(color);
};

const focusConfirmButton = () => {
  closeSearchDropdown();
  confirmButtonRef.value?.focus();
};

const focusSearchInput = () => {
  searchInputRef.value?.focus();
};

watch(searchQuery, () => {
  highlightedSearchIndex.value = -1;
});

const goToStep2 = () => {
  if (!localSelectedIds.value.length) return;
  currentStep.value = 2;
  activeColorId.value = localSelectedIds.value[0];
};

// ===== Helpers pour les photos par couleur =====

const getOrCreateColorEntry = (colorId: number): ColorPhotoEntry => {
  if (!colorPhotos.value.has(colorId)) {
    colorPhotos.value.set(colorId, {
      files: [],
      previews: new Map(),
      rotations: new Map(),
      coverIndex: 0,
    });
  }
  return colorPhotos.value.get(colorId)!;
};

const getColorLabel = (colorId: number) =>
  props.colors.find((c) => c.id_couleur === colorId)?.label ?? "";

const getColorFiles = (colorId: number) =>
  colorPhotos.value.get(colorId)?.files ?? [];

const getColorPhotoCount = (colorId: number) =>
  getColorFiles(colorId).length;

const getColorFilePreview = (colorId: number, file: File) =>
  colorPhotos.value.get(colorId)?.previews.get(file) ?? "";

const getColorFileRotation = (colorId: number, file: File) =>
  colorPhotos.value.get(colorId)?.rotations.get(file) ?? 0;

const getColorCoverIndex = (colorId: number) =>
  colorPhotos.value.get(colorId)?.coverIndex ?? 0;

const allColorsHavePhotos = computed(() =>
  localSelectedIds.value.every((id) => getColorPhotoCount(id) > 0)
);

const addFilesForColor = (colorId: number, files: File[]) => {
  const entry = getOrCreateColorEntry(colorId);
  files
    .filter((f) => ALLOWED_PHOTO_TYPES.has(f.type.toLowerCase()))
    .forEach((file) => {
      entry.files.push(file);
      entry.previews.set(file, URL.createObjectURL(file));
    });
};

const triggerFileDialogForColor = (colorId: number) => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/jpeg,image/png,image/gif,image/webp,.dng";
  input.multiple = true;
  input.addEventListener("change", (event) => {
    const target = event.target as HTMLInputElement;
    if (target?.files) addFilesForColor(colorId, Array.from(target.files));
    input.remove();
  });
  input.click();
};

const handleDropForColor = (event: DragEvent, colorId: number) => {
  isDragging.value = false;
  if (event.dataTransfer?.files)
    addFilesForColor(colorId, Array.from(event.dataTransfer.files));
};

const rotateColorPhoto = (colorId: number, index: number) => {
  const entry = colorPhotos.value.get(colorId);
  if (!entry) return;
  const file = entry.files[index];
  const current = entry.rotations.get(file) ?? 0;
  entry.rotations.set(file, (current + 90) % 360);
};

const setColorCover = (colorId: number, index: number) => {
  const entry = colorPhotos.value.get(colorId);
  if (entry) entry.coverIndex = index;
};

// Drag & drop pour réordonner les photos d'une couleur (déposer en 1ère position = couverture)
const draggingColorPhotoIndex = ref<number | null>(null);
const dragOverColorPhotoIndex = ref<number | null>(null);

const handleColorPhotoDragStart = (event: DragEvent, index: number) => {
  draggingColorPhotoIndex.value = index;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(index));
  }
};

const handleColorPhotoDragEnd = () => {
  draggingColorPhotoIndex.value = null;
  dragOverColorPhotoIndex.value = null;
};

const handleColorPhotoDragOver = (event: DragEvent, index: number) => {
  if (draggingColorPhotoIndex.value === null) return;
  event.preventDefault();
  event.stopPropagation();
  dragOverColorPhotoIndex.value = index;
  if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
};

const handleColorPhotoDrop = (event: DragEvent, colorId: number, dropIndex: number) => {
  event.preventDefault();
  event.stopPropagation();
  const dragIndex = draggingColorPhotoIndex.value;
  draggingColorPhotoIndex.value = null;
  dragOverColorPhotoIndex.value = null;
  if (dragIndex === null || dragIndex === dropIndex) return;
  const entry = colorPhotos.value.get(colorId);
  if (!entry) return;
  // Mémoriser le fichier de couverture courant pour le suivre après réorganisation
  const currentCoverFile = entry.files[entry.coverIndex] ?? null;
  const [moved] = entry.files.splice(dragIndex, 1);
  entry.files.splice(dropIndex, 0, moved);
  // Déposer en 1ère position => devient la photo de couverture
  if (dropIndex === 0) {
    entry.coverIndex = 0;
  } else if (currentCoverFile) {
    const newCoverIndex = entry.files.indexOf(currentCoverFile);
    entry.coverIndex = newCoverIndex >= 0 ? newCoverIndex : 0;
  }
};

const removeColorFile = (colorId: number, index: number) => {
  const entry = colorPhotos.value.get(colorId);
  if (!entry) return;
  const file = entry.files[index];
  const preview = entry.previews.get(file);
  if (preview) {
    URL.revokeObjectURL(preview);
    entry.previews.delete(file);
  }
  entry.rotations.delete(file);
  entry.files.splice(index, 1);
  if (entry.coverIndex >= entry.files.length) {
    entry.coverIndex = Math.max(0, entry.files.length - 1);
  } else if (index < entry.coverIndex) {
    entry.coverIndex--;
  }
};

// ===== Mode mélangées =====

const triggerFileDialogMelangees = () => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/jpeg,image/png,image/gif,image/webp,.dng";
  input.multiple = true;
  input.addEventListener("change", (event) => {
    const target = event.target as HTMLInputElement;
    if (target?.files) addMelangeesFiles(Array.from(target.files));
    input.remove();
  });
  input.click();
};

const handleDropMelangees = (event: DragEvent) => {
  isDragging.value = false;
  if (event.dataTransfer?.files)
    addMelangeesFiles(Array.from(event.dataTransfer.files));
};

const addMelangeesFiles = (files: File[]) => {
  files
    .filter((f) => ALLOWED_PHOTO_TYPES.has(f.type.toLowerCase()))
    .forEach((file) => {
      melangeesFiles.value.push(file);
      melangeesFilePreviews.value.set(file, URL.createObjectURL(file));
    });
};

const getMelangeesPreview = (file: File) =>
  melangeesFilePreviews.value.get(file) ?? "";

const getMelangeesRotation = (file: File) =>
  melangeesFileRotations.value.get(file) ?? 0;

const rotateMelangeesPhoto = (index: number) => {
  const file = melangeesFiles.value[index];
  const current = melangeesFileRotations.value.get(file) ?? 0;
  melangeesFileRotations.value.set(file, (current + 90) % 360);
};

const removeMelangeesFile = (index: number) => {
  const file = melangeesFiles.value[index];
  const preview = melangeesFilePreviews.value.get(file);
  if (preview) {
    URL.revokeObjectURL(preview);
    melangeesFilePreviews.value.delete(file);
  }
  melangeesFileRotations.value.delete(file);
  melangeesFiles.value.splice(index, 1);
  if (melangesCoverIndex.value >= melangeesFiles.value.length) {
    melangesCoverIndex.value = Math.max(0, melangeesFiles.value.length - 1);
  } else if (index < melangesCoverIndex.value) {
    melangesCoverIndex.value--;
  }
};

const handleClose = () => {
  emit("update:visible", false);
};

const handleSaveAndPublish = async () => {
  if (!props.product || !localSelectedIds.value.length) return;
  if (!allColorsHavePhotos.value) return;

  const variants = localSelectedIds.value.map((colorId) => {
    const entry = colorPhotos.value.get(colorId);
    const label = getColorLabel(colorId);
    const files = entry?.files ?? [];

    let orderedFiles = [...files];
    const coverIdx = entry?.coverIndex ?? 0;
    if (coverIdx > 0 && orderedFiles.length > 1) {
      const cover = orderedFiles.splice(coverIdx, 1)[0];
      orderedFiles.unshift(cover);
    }

    const rotations = orderedFiles.map(
      (file) => entry?.rotations.get(file) ?? 0,
    );

    return {
      couleurId: colorId,
      couleurName: label,
      photos: orderedFiles,
      rotations,
    };
  });

  emit("saveAndPublish", {
    productId: duplicateSourceProductId.value,
    variants,
  });

  // Cleanup
  colorPhotos.value.forEach((entry) => {
    entry.previews.forEach((url) => URL.revokeObjectURL(url));
  });
  colorPhotos.value = new Map();
  localSelectedIds.value = [];
  emit("update:visible", false);
};

const handleSave = async () => {
  if (!props.product) return;

  if (props.venduPar === "melangees") {
    if (!melangeesFiles.value.length) return;

    let orderedPhotos = [...melangeesFiles.value];
    if (melangesCoverIndex.value > 0 && orderedPhotos.length > 1) {
      const cover = orderedPhotos.splice(melangesCoverIndex.value, 1)[0];
      orderedPhotos.unshift(cover);
    }
    const rotations = orderedPhotos.map(
      (file) => melangeesFileRotations.value.get(file) ?? 0,
    );

    emit("save", {
      productId: duplicateSourceProductId.value,
      variants: [
        {
          couleurId: 0,
          couleurName: "",
          photos: orderedPhotos,
          rotations,
        },
      ],
    });

    melangeesFilePreviews.value.forEach((url) => URL.revokeObjectURL(url));
    melangeesFilePreviews.value.clear();
    melangeesFiles.value = [];
    emit("update:visible", false);
  } else {
    if (!localSelectedIds.value.length) return;

    const variants = localSelectedIds.value.map((colorId) => {
      const entry = colorPhotos.value.get(colorId);
      const label = getColorLabel(colorId);
      const files = entry?.files ?? [];

      let orderedFiles = [...files];
      const coverIdx = entry?.coverIndex ?? 0;
      if (coverIdx > 0 && orderedFiles.length > 1) {
        const cover = orderedFiles.splice(coverIdx, 1)[0];
        orderedFiles.unshift(cover);
      }

      const rotations = orderedFiles.map(
        (file) => entry?.rotations.get(file) ?? 0,
      );

      return {
        couleurId: colorId,
        couleurName: label,
        photos: orderedFiles.length ? orderedFiles : undefined,
        rotations: orderedFiles.length ? rotations : undefined,
      };
    });

    emit("save", {
      productId: duplicateSourceProductId.value,
      variants,
    });

    // Cleanup
    colorPhotos.value.forEach((entry) => {
      entry.previews.forEach((url) => URL.revokeObjectURL(url));
    });
    colorPhotos.value = new Map();
    localSelectedIds.value = [];
    emit("update:visible", false);
  }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.collapse-panel {
  display: grid;
  transition:
    grid-template-rows 0.28s ease,
    opacity 0.22s ease;
}

.collapse-panel--open {
  grid-template-rows: 1fr;
  opacity: 1;
}

.collapse-panel--closed {
  grid-template-rows: 0fr;
  opacity: 0.72;
}

.collapse-panel__inner {
  overflow: hidden;
  min-height: 0;
}

.collapse-panel__inner--sidebar > button {
  animation: collapse-item-in 0.24s ease both;
}

.collapse-panel--open .collapse-panel__inner--sidebar > button:nth-child(1) {
  animation-delay: 0.02s;
}
.collapse-panel--open .collapse-panel__inner--sidebar > button:nth-child(2) {
  animation-delay: 0.04s;
}
.collapse-panel--open .collapse-panel__inner--sidebar > button:nth-child(3) {
  animation-delay: 0.06s;
}
.collapse-panel--open .collapse-panel__inner--sidebar > button:nth-child(4) {
  animation-delay: 0.08s;
}
.collapse-panel--open .collapse-panel__inner--sidebar > button:nth-child(n + 5) {
  animation-delay: 0.1s;
}

.collapse-chevron {
  transition: transform 0.28s ease;
}

.collapse-chevron--open {
  transform: rotate(180deg);
}

@keyframes collapse-item-in {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.color-swatch-dot {
  display: inline-block;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.18);
  background-repeat: no-repeat;
  background-size: 100% 100%;
  background-clip: padding-box;
}
</style>
